import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { data, portal, type Connection } from "@aawaz/api-client";
import {
  Badge, Button, Card, ChannelLogo, CopyButton, EmptyState, ErrorBanner, Field, Icon, Input, Meter, Modal, Notice, Select, SkeletonRows, Table, fmtDate,
  initials, maskPhone,
} from "@aawaz/ui";
import { useClients } from "../components/ItemPanel";
import { useSession } from "../session";

interface Group { binding_id: string; error: string | null; created_at: string; group_id: string | null; status: string; client_org_id: string; connection_id: string; subject: string | null; invite_link: string | null; pending_join_requests: number }
interface JoinReq { id: string; phone: string; status: string; created_at: string }
interface Sender { id: string; external_ref: string; client_org_id: string; binding_type: string }
const GROUP_LIMIT = 10_000;
/** WhatsApp confirms a new group within seconds; after ten minutes it is not coming. */
const stuck = (g: Group) => Date.now() - new Date(g.created_at).getTime() > 10 * 60_000;

/** WhatsApp: the firm's numbers, one group per client (created by the connector), direct senders mapped to clients,
 *  and (CA edition) staff numbers that forward bills for any client. */
export function WhatsAppPage() {
  const s = useSession();
  const qc = useQueryClient();
  const clients = useClients();
  // a new group is confirmed by WhatsApp a few seconds later: keep looking while one is being created
  const groups = useQuery({ queryKey: ["wa-groups"], queryFn: () => data<{ data: Group[] }>(portal.GET("/portal/v1/whatsapp/groups")),
    refetchInterval: (q) => (q.state.data as { data: Group[] } | undefined)?.data.some((g) => g.status === "creating") ? 5_000 : false });
  const conns = useQuery({ queryKey: ["connections"], queryFn: () => data<{ data: Connection[] }>(portal.GET("/portal/v1/connections")) });
  const senders = useQuery({ queryKey: ["bindings", "senders"], queryFn: () => data<{ data: Sender[] }>(portal.GET("/portal/v1/bindings")) });
  // a set-up that was started and never finished is not a connection: nothing can be linked to it
  const waConns = (conns.data?.data ?? []).filter((c) => c.connector_key === "whatsapp" && ["active", "degraded"].includes(c.status));
  const connect = useMutation({
    mutationFn: () => data(portal.POST("/portal/v1/connections", { body: { connector_key: "whatsapp", config: {} } })),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["connections"] }), qc.invalidateQueries({ queryKey: ["dashboard"] })]),
  });
  const [creating, setCreating] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [requestsFor, setRequestsFor] = useState<Group | null>(null);
  const [unlinking, setUnlinking] = useState<Sender | null>(null);
  const unlink = useMutation({
    mutationFn: (id: string) => portal.DELETE("/portal/v1/bindings/{binding_id}", { params: { path: { binding_id: id } } }),
    onSuccess: async () => { setUnlinking(null); await qc.invalidateQueries({ queryKey: ["bindings"] }); },
  });
  const clearGroup = useMutation({
    mutationFn: (id: string) => portal.DELETE("/portal/v1/whatsapp/groups/{binding_id}", { params: { path: { binding_id: id } } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wa-groups"] }),
  });
  const newLink = useMutation({
    mutationFn: (id: string) => data(portal.POST("/portal/v1/whatsapp/groups/{group_id}/invite-link:reset", { params: { path: { group_id: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wa-groups"] }),
  });
  // the AawazAI number is shared by every firm: only what this firm linked (a group, a number) reaches it
  const shared = waConns.some((c) => c.shared);
  const number = s.me?.whatsapp?.number ?? null;
  // WhatsApp lets only an Official Business Account create groups; until the shared number has that, groups are off
  const groupsOn = !shared || !!s.me?.whatsapp?.groups;
  const forClients = `Please send your bills, invoices and bank statements on WhatsApp to ${number ?? "the AawazAI number"}` +
    `${s.me?.firm ? ` (AawazAI, for ${s.me.firm.name})` : ""}. Send each as a PDF or a clear photo, from the number you gave us. You get a receipt for every file.`;
  const names = new Map((clients.data?.data ?? []).map((c) => [c.id, c.display_name]));
  const perConn = (id: string) => (groups.data?.data ?? []).filter((g) => g.connection_id === id).length;
  const direct = (senders.data?.data ?? []).filter((b) => b.binding_type === "whatsapp_sender" && b.client_org_id);
  if (!conns.isLoading && !waConns.length) {
    return (
      <Card>
        <ErrorBanner error={connect.error} />
        <EmptyState icon="message" title="WhatsApp is not connected"
                    action={s.can("manage_connections") ? <Button variant="primary" busy={connect.isPending} onClick={() => connect.mutate()}>Connect WhatsApp</Button> : undefined}>
          One click, nothing to enter. Your firm then receives what your clients and your team send to the AawazAI number{number ? ` (${number})` : ""}.
        </EmptyState>
      </Card>
    );
  }
  return (
    <>
      <div className="ui-tiles">
        {waConns.map((c) => (
          <div key={c.id} className="ui-card ui-tile">
            <div className="ui-tile-head">
              <span className="ui-tile-logo"><ChannelLogo kind="whatsapp" size={26} /></span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <span className="ui-tile-eyebrow">{c.shared ? "AawazAI number" : "Firm number"}</span>
                <strong className="ui-tile-name">{c.shared && number ? number : c.display_name.toLowerCase() === "whatsapp" ? (c.shared ? "WhatsApp" : "WhatsApp Business") : c.display_name}</strong>
              </div>
              <span className={`ui-dot ui-${c.status === "active" ? "green" : "amber"}`} />
            </div>
            {c.shared ? <p>{perConn(c.id).toLocaleString("en-IN")} client group{perConn(c.id) === 1 ? "" : "s"} · {direct.length} mapped number{direct.length === 1 ? "" : "s"}</p>
              : <Meter value={perConn(c.id) / GROUP_LIMIT}
                   label={<>{perConn(c.id).toLocaleString("en-IN")} of {GROUP_LIMIT.toLocaleString("en-IN")} groups{perConn(c.id) >= GROUP_LIMIT * 0.95 ? " · add another number" : ""}</>} />}
          </div>
        ))}
        <div className="ui-card ui-tile ui-tile-muted">
          <div className="ui-tile-head"><span className="ui-tile-logo"><Icon name="users" size={20} /></span>
            <div><span className="ui-tile-eyebrow">Mapped</span><strong className="ui-tile-name">{direct.length} direct sender{direct.length === 1 ? "" : "s"}</strong></div></div>
          <p>People who message the {shared ? "AawazAI" : "firm"} number directly, filed under their client without asking.</p>
        </div>
      </div>

      <ErrorBanner error={groups.error ?? senders.error ?? clearGroup.error ?? newLink.error} />
      {shared ? (
        <Card title="What to tell your clients" subtitle="A client's files reach your firm only from a number mapped to that client below (or from your staff numbers). Map the number first, then send them this."
              actions={<CopyButton text={forClients} label="Copy message" />}>
          <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{forClients}</p>
        </Card>
      ) : null}
      <Card title="Client groups" subtitle="A group per client with your team and the AawazAI number. Anything shared there is read and filed under that client."
            actions={s.can("whatsapp") && groupsOn ? <Button variant="primary" size="sm" icon="plus" onClick={() => setCreating(true)}>New group</Button> : null} flush>
        {groups.isLoading ? <div style={{ padding: 22 }}><SkeletonRows rows={4} /></div> : (groups.data?.data.length ?? 0) === 0 ? (
          !groupsOn ? (
            <EmptyState icon="users" title="Groups are not available yet">
              WhatsApp must first approve the AawazAI number as an Official Business Account. Until then, map each client's own number under Direct senders; it works the same way for one-to-one chats.
            </EmptyState>
          ) :
          <EmptyState icon="users" title="No groups yet"
            action={s.can("whatsapp") ? <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>Create the first group</Button> : undefined}>
            Groups keep each client's bills apart, even when the same person works with several clients.
          </EmptyState>
        ) : (
          <Table rows={groups.data!.data} rowKey={(r) => r.binding_id} columns={[
            { key: "c", header: "Client", render: (r) => (
              <div className="ui-cell-main"><span className="ui-avatar ui-avatar-soft" aria-hidden="true">{initials(names.get(r.client_org_id) ?? "?")}</span>
                <div style={{ minWidth: 0 }}><span className="ui-cell-title">{names.get(r.client_org_id) ?? "…"}</span>
                  <span className="ui-cell-sub">{r.subject ?? r.group_id}</span></div></div>) },
            { key: "s", header: "Status", render: (r) => r.status === "failed"
              ? <><Badge value="failed" tone="red" /><div className="ui-hint">{r.error}</div></>
              : r.status === "creating"
                ? <><Badge value="creating" /><div className="ui-hint">{stuck(r) ? "WhatsApp has not answered. Remove it and create the group again." : "Waiting for WhatsApp, usually a few seconds."}</div></>
                : <Badge value={r.status} tone={r.status === "active" ? "green" : undefined} /> },
            { key: "l", header: "Invite link", render: (r) => r.invite_link ? (
                <span className="ui-row" style={{ gap: 6 }}><CopyButton text={r.invite_link} label="Copy link" />
                  {s.can("whatsapp") && r.group_id ? <Button size="sm" variant="ghost" busy={newLink.isPending && newLink.variables === r.group_id}
                    onClick={() => confirm("Make a new invite link? The current link stops working.") && newLink.mutate(r.group_id!)}>New link</Button> : null}</span>)
              : s.can("whatsapp") && (r.status === "failed" || (r.status === "creating" && stuck(r)))
                ? <Button size="sm" variant="ghost" busy={clearGroup.isPending && clearGroup.variables === r.binding_id} onClick={() => clearGroup.mutate(r.binding_id)}>Remove</Button>
                : <span className="ui-hint">—</span> },
            { key: "j", header: "Join requests", render: (r) => r.pending_join_requests && r.group_id
              ? <Button size="sm" onClick={() => setRequestsFor(r)}>{r.pending_join_requests} waiting</Button> : <span className="ui-hint">None</span> },
          ]} />
        )}
      </Card>

      <Card title="Direct senders" subtitle={`When a client messages the ${shared ? "AawazAI" : "firm"} number directly, their files go to the client mapped here.`}
            actions={s.can("manage_connections") ? <Button size="sm" icon="plus" onClick={() => setMapping(true)}>Map a number</Button> : null} flush>
        {senders.isLoading ? <div style={{ padding: 22 }}><SkeletonRows rows={2} /></div> : direct.length === 0 ? (
          <p className="ui-hint" style={{ margin: 0, padding: "0 22px 20px" }}>{shared
            ? "No numbers mapped. Other firms use this number too, so only numbers mapped here, staff numbers and your client groups reach your firm."
            : "No numbers mapped. Unmapped senders are matched by the GSTIN on their bills, or wait in the inbox."}</p>
        ) : (
          <Table rows={direct} rowKey={(r) => r.id} columns={[
            { key: "p", header: "Number", render: (r) => <span className="ui-mono">{maskPhone(r.external_ref)}</span> },
            { key: "c", header: "Client", render: (r) => <span className="ui-tag ui-tag-brand">{names.get(r.client_org_id) ?? "…"}</span> },
            { key: "x", header: "", render: (r) => s.can("manage_connections")
              ? <Button size="sm" variant="ghost" onClick={() => setUnlinking(r)}>Unlink</Button> : null },
          ]} />
        )}
      </Card>
      <Modal open={!!unlinking} size="sm" title="Unlink this number?" onClose={() => setUnlinking(null)}>
        <ErrorBanner error={unlink.error} />
        <p style={{ margin: 0 }}>Files <strong className="ui-mono">{unlinking ? maskPhone(unlinking.external_ref) : ""}</strong> sends
          directly will no longer be filed under {unlinking ? names.get(unlinking.client_org_id) ?? "this client" : ""}. What it already sent is kept.</p>
        <div className="ui-dialog-actions">
          <Button variant="ghost" onClick={() => setUnlinking(null)}>Keep</Button>
          <Button variant="danger" busy={unlink.isPending} onClick={() => unlinking && unlink.mutate(unlinking.id)}>Unlink</Button>
        </div>
      </Modal>

      {s.has("ca") ? <StaffNumbers canManage={s.can("manage_connections")} /> : null}

      <CreateGroup open={creating} onClose={() => setCreating(false)} waConns={waConns} />
      <MapSender open={mapping} onClose={() => setMapping(false)} connectionId={waConns[0]?.id ?? ""} />
      <Modal open={!!requestsFor} size="md" title="Join requests" icon={<ChannelLogo kind="whatsapp" size={24} />}
             subtitle={requestsFor ? `${names.get(requestsFor.client_org_id) ?? ""} · ${requestsFor.subject ?? ""}` : undefined} onClose={() => setRequestsFor(null)}>
        {requestsFor?.group_id ? <JoinRequests groupId={requestsFor.group_id} onClose={() => setRequestsFor(null)} /> : null}
      </Modal>
    </>
  );
}

function CreateGroup({ open, onClose, waConns }: { open: boolean; onClose: () => void; waConns: Connection[] }) {
  const qc = useQueryClient();
  const clients = useClients();
  const [form, setForm] = useState({ connection_id: "", client_org_id: "", name: "" });
  const create = useMutation({
    mutationFn: () => data(portal.POST("/portal/v1/whatsapp/groups", { body: {
      connection_id: form.connection_id || waConns[0]?.id || "", client_org_id: form.client_org_id, name: form.name || null } })),
    onSuccess: async () => { setForm({ connection_id: "", client_org_id: "", name: "" }); onClose(); await qc.invalidateQueries({ queryKey: ["wa-groups"] }); },
  });
  const client = clients.data?.data.find((c) => c.id === form.client_org_id);
  return (
    <Modal open={open} size="sm" title="New client group" icon={<ChannelLogo kind="whatsapp" size={24} />}
           subtitle="AawazAI creates the group and gives you an invite link to share with the client." onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <ErrorBanner error={create.error} />
        {waConns.length > 1 ? <Field label="From number">{(id) => <Select id={id} value={form.connection_id} onChange={(e) => setForm({ ...form, connection_id: e.target.value })}
          options={waConns.map((c) => ({ value: c.id, label: c.display_name }))} />}</Field> : null}
        <Field label="Client">{(id) => <Select id={id} required value={form.client_org_id} onChange={(e) => setForm({ ...form, client_org_id: e.target.value })}
          options={[{ value: "", label: "Choose client" }, ...(clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.display_name }))]} />}</Field>
        <Field label="Group name" hint={`Leave empty for “${client?.display_name ?? "Client"} × your firm | AawazAI”.`}>{(id) =>
          <Input id={id} maxLength={100} value={form.name} placeholder="Optional" onChange={(e) => setForm({ ...form, name: e.target.value })} />}</Field>
        <ul className="ui-checklist" style={{ marginTop: 4 }}>
          <li><Icon name="check" size={16} />WhatsApp allows up to 8 members in a group a business creates, including the AawazAI number.</li>
          <li><Icon name="check" size={16} />Members join with the invite link; you approve requests here.</li>
        </ul>
        <div className="ui-dialog-actions">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" busy={create.isPending} disabled={!form.client_org_id}>Create group</Button>
        </div>
      </form>
    </Modal>
  );
}

function MapSender({ open, onClose, connectionId }: { open: boolean; onClose: () => void; connectionId: string }) {
  const qc = useQueryClient();
  const clients = useClients();
  const [sender, setSender] = useState({ phone: "+91", client_org_id: "" });
  const map = useMutation({
    mutationFn: () => data(portal.POST("/portal/v1/bindings", { body: { connection_id: connectionId,
      binding_type: "whatsapp_sender", external_ref: sender.phone, client_org_id: sender.client_org_id, config: {} } })),
    onSuccess: async () => { setSender({ phone: "+91", client_org_id: "" }); onClose(); await qc.invalidateQueries({ queryKey: ["bindings"] }); },
  });
  return (
    <Modal open={open} size="sm" title="Map a number to a client" icon={<Icon name="users" size={20} />}
           subtitle="Files this number sends straight to the firm number are filed under the client you choose." onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); map.mutate(); }}>
        <ErrorBanner error={map.error} />
        <Field label="WhatsApp number">{(id) => <Input id={id} type="tel" value={sender.phone} onChange={(e) => setSender({ ...sender, phone: e.target.value })} />}</Field>
        <Field label="Client">{(id) => <Select id={id} value={sender.client_org_id} onChange={(e) => setSender({ ...sender, client_org_id: e.target.value })}
          options={[{ value: "", label: "Choose client" }, ...(clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.display_name }))]} />}</Field>
        <div className="ui-dialog-actions">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" busy={map.isPending} disabled={!sender.client_org_id || sender.phone.replace(/\D/g, "").length < 10}>Map number</Button>
        </div>
      </form>
    </Modal>
  );
}

interface Staff { binding_id: string; phone: string; name: string | null; active: boolean; created_at: string }

/** CA staff forward bills for many clients from their own phones: AawazAI files each bill by the GSTIN on it,
 *  a #code caption or the last client picked, and otherwise asks the staff member once per batch. */
function StaffNumbers({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const staff = useQuery({ queryKey: ["ca-staff"], queryFn: () => data<{ data: Staff[] }>(portal.GET("/portal/v1/ca/staff")) });
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Staff | null>(null);
  const [form, setForm] = useState({ phone: "", name: "" });
  const add = useMutation({
    mutationFn: () => data(portal.POST("/portal/v1/ca/staff", { body: { phone: form.phone, name: form.name || null } })),
    onSuccess: async () => { setForm({ phone: "", name: "" }); setAdding(false); await qc.invalidateQueries({ queryKey: ["ca-staff"] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => portal.DELETE("/portal/v1/ca/staff/{binding_id}", { params: { path: { binding_id: id } } }),
    onSuccess: async () => { setRemoving(null); await qc.invalidateQueries({ queryKey: ["ca-staff"] }); },
  });
  return (
    <Card title="Staff numbers" subtitle="Your team forwards bills for any client; AawazAI works out whose each one is."
          actions={canManage ? <Button size="sm" icon="plus" onClick={() => setAdding(true)}>Add staff</Button> : null} flush>
      <ErrorBanner error={remove.error ?? staff.error} />
      {staff.isLoading ? <div style={{ padding: 22 }}><SkeletonRows rows={2} /></div> : (staff.data?.data.length ?? 0) === 0 ? (
        <p className="ui-hint" style={{ margin: 0, padding: "0 22px 20px" }}>No staff numbers yet.</p>
      ) : (
        <Table rows={staff.data!.data} rowKey={(r) => r.binding_id} columns={[
          { key: "n", header: "Staff", render: (r) => (
            <div className="ui-cell-main"><span className="ui-avatar ui-avatar-soft" aria-hidden="true">{initials(r.name ?? r.phone.slice(-2))}</span>
              <div><span className="ui-cell-title">{r.name ?? "Unnamed"}</span><span className="ui-cell-sub ui-mono">{r.phone}</span></div></div>) },
          { key: "d", header: "Added", render: (r) => <span className="ui-hint">{fmtDate(r.created_at)}</span> },
          { key: "x", header: "", render: (r) => canManage ? <Button size="sm" variant="ghost" onClick={() => setRemoving(r)}>Remove</Button> : null },
        ]} />
      )}
      <Modal open={adding} size="sm" title="Add a staff number" icon={<Icon name="users" size={20} />} onClose={() => setAdding(false)}
             subtitle="Bills forwarded from this number are filed by the GSTIN on the bill, a caption like #ramesh, or the client picked in the last 10 minutes. Otherwise AawazAI asks once per batch.">
        <form onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
          <ErrorBanner error={add.error} />
          <Field label="WhatsApp number">{(id) => <Input id={id} type="tel" placeholder="98765 43210" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />}</Field>
          <Field label="Name" hint="Optional; shown in the inbox next to what they sent.">{(id) => <Input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}</Field>
          <div className="ui-dialog-actions">
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button variant="primary" type="submit" busy={add.isPending} disabled={form.phone.replace(/\D/g, "").length < 10}>Add staff</Button>
          </div>
        </form>
      </Modal>
      <Modal open={!!removing} size="sm" title="Remove staff number?" onClose={() => setRemoving(null)}>
        <p style={{ margin: 0 }}>Bills from <strong className="ui-mono">{removing?.phone}</strong> will no longer be filed for any client automatically.</p>
        <div className="ui-dialog-actions">
          <Button variant="ghost" onClick={() => setRemoving(null)}>Keep</Button>
          <Button variant="danger" busy={remove.isPending} onClick={() => removing && remove.mutate(removing.binding_id)}>Remove</Button>
        </div>
      </Modal>
    </Card>
  );
}

function JoinRequests({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["wa-join", groupId], queryFn: () => data<{ data: JoinReq[] }>(portal.GET(
    "/portal/v1/whatsapp/groups/{group_id}/join-requests", { params: { path: { group_id: groupId } } })) });
  const decide = useMutation({
    mutationFn: ({ id, d }: { id: string; d: "approve" | "reject" }) => data(portal.POST(
      "/portal/v1/whatsapp/groups/{group_id}/join-requests/{req_id}:{decision}",
      { params: { path: { group_id: groupId, req_id: id, decision: d } } })),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["wa-join", groupId] }); await qc.invalidateQueries({ queryKey: ["wa-groups"] }); },
  });
  const rows = q.data?.data ?? [];
  return (
    <>
      <ErrorBanner error={decide.error ?? q.error} />
      {q.isLoading ? <SkeletonRows rows={3} /> : rows.length === 0 ? <Notice>No requests waiting.</Notice> : (
        <div className="ui-pick-list">
          {rows.map((r) => (
            <div key={r.id} className="ui-pick-row">
              <span className="ui-pick-icon"><Icon name="user" size={16} /></span>
              <div className="ui-pick-name"><span className="ui-mono">{maskPhone(r.phone)}</span>
                <span className="ui-cell-sub">Asked {fmtDate(r.created_at)}</span></div>
              {r.status === "pending" ? (
                <div className="ui-pick-bind">
                  <Button size="sm" variant="ghost" busy={decide.isPending && decide.variables?.id === r.id && decide.variables.d === "reject"}
                          onClick={() => decide.mutate({ id: r.id, d: "reject" })}>Decline</Button>
                  <Button size="sm" variant="primary" busy={decide.isPending && decide.variables?.id === r.id && decide.variables.d === "approve"}
                          onClick={() => decide.mutate({ id: r.id, d: "approve" })}>Let in</Button>
                </div>
              ) : <Badge value={r.status} />}
            </div>
          ))}
        </div>
      )}
      <div className="ui-dialog-actions"><Button variant="primary" onClick={onClose}>Done</Button></div>
    </>
  );
}
